/**
 * The board half of the ★ / team play overlay: a translucent scrim over the
 * live map with the rule territories cut out and outlined, plus example
 * arrows, pulse rings and tags (design v3, Variant 1).
 *
 * Drawn in map units inside one of MapView's overlay <Svg>s, under the same
 * zoom/pan group as the board, so pinch and drag keep working while it is
 * open. Pixel sizes from the model are converted with `unitsPerPixel` at the
 * current zoom. Never takes touches.
 */
import React from 'react'
import { Platform } from 'react-native'
import { Circle, ClipPath, Defs, G, Mask, Path, Polygon, Rect, Text as SvgText } from 'react-native-svg'
import { markVisible, type ChipGlyph, type OverlayMark, type OverlayModel, type Pt } from '../../../lib/ruleOverlayModel'
import type { OverlayPalette } from '../../../lib/rulesOverlayTheme'
import { GlyphShapes, TAG_ICON_PATHS } from './RuleGlyph'

export interface RulesOverlayBoardLayerProps {
  model: OverlayModel
  isolatedRow: string | null
  territoryShapeById: Record<string, string | undefined>
  regionShapeById: Record<string, string | undefined>
  /** Territories of each region, for regions without a shape of their own. */
  regionTerritoryIds: Record<string, string[]>
  viewBox: { x: number; y: number; width: number; height: number }
  /** Map units per screen pixel at the current zoom. */
  unitsPerPixel: number
  palette: OverlayPalette
  /** Unique per <Svg> (web shares one id space per document). */
  idPrefix: string
  compact: boolean
}

interface ArrowGeom { path: string; head: string | null; mid: Pt; c: Pt; q: Pt }

/** An SVG path's rings as point lists (M/L/Z polygons, as the map shapes are). */
function pathRings(d: string): Pt[][] {
  return d.split(/[Zz]/).map((sub) => {
    const n = (sub.match(/-?\d+(?:\.\d+)?/g) ?? []).map(Number)
    const out: Pt[] = []
    for (let i = 0; i + 1 < n.length; i += 2) out.push({ x: n[i]!, y: n[i + 1]! })
    return out
  }).filter((r) => r.length >= 3)
}

/** Even-odd point-in-shape over every ring (holes included). */
function insideRings(rings: Pt[][], p: Pt): boolean {
  let inside = false
  for (const r of rings) {
    for (let i = 0, j = r.length - 1; i < r.length; j = i++) {
      const a = r[i]!, b = r[j]!
      if ((a.y > p.y) !== (b.y > p.y) && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) inside = !inside
    }
  }
  return inside
}

/**
 * Where an avoid-lit tag goes: right of `at` when that is clear, else the
 * nearest clear spot round it (in angle, then distance); failing that, the
 * spot that covers least. `hit` samples the tag's box for lit ground.
 */
function placeClear(at: Pt, halfW: number, halfH: number, gap: number, hit: (p: Pt) => boolean): Pt {
  const angles = [0, 25, -25, 50, -50, 75, -75, 100, -100, 130, -130, 180]
  let best = { x: at.x + gap + halfW, y: at.y }
  let bestScore = Infinity
  for (const step of [1, 1.5, 2.1, 2.8, 3.6]) {
    for (const deg of angles) {
      const a = (deg * Math.PI) / 180
      // Far enough along the angle that the box clears `at` by `gap`.
      const reach = (gap + Math.min(Math.abs(halfW / (Math.cos(a) || 1e-6)), Math.abs(halfH / (Math.sin(a) || 1e-6)))) * step
      const c = { x: at.x + Math.cos(a) * reach, y: at.y + Math.sin(a) * reach }
      let score = 0
      for (let i = 0; i <= 6; i++) for (let j = 0; j <= 2; j++) {
        if (hit({ x: c.x - halfW + (2 * halfW * i) / 6, y: c.y - halfH + (2 * halfH * j) / 2 })) score++
      }
      if (score === 0) return c
      if (score < bestScore) { bestScore = score; best = c }
    }
  }
  return best
}

/** A quadratic arrow from p to q (design `arrow()`), in map units. */
export function arrowGeometry(p: Pt, q: Pt, bulge: number, startGap: number, endGap: number, headSize: number, head: boolean): ArrowGeom {
  const dx = q.x - p.x
  const dy = q.y - p.y
  const len = Math.hypot(dx, dy) || 1
  const nx = -dy / len
  const ny = dx / len
  const c = { x: (p.x + q.x) / 2 + nx * len * bulge, y: (p.y + q.y) / 2 + ny * len * bulge }
  const toward = (a: Pt, b: Pt, d: number): Pt => {
    const vx = b.x - a.x
    const vy = b.y - a.y
    const l = Math.hypot(vx, vy) || 1
    return { x: a.x + (vx / l) * d, y: a.y + (vy / l) * d }
  }
  const P = toward(p, c, startGap)
  const Q = toward(q, c, endGap)
  let headPoints: string | null = null
  if (head) {
    const vx = Q.x - c.x
    const vy = Q.y - c.y
    const l = Math.hypot(vx, vy) || 1
    const ux = vx / l
    const uy = vy / l
    const a = { x: Q.x - ux * headSize + uy * headSize * 0.62, y: Q.y - uy * headSize - ux * headSize * 0.62 }
    const b = { x: Q.x - ux * headSize - uy * headSize * 0.62, y: Q.y - uy * headSize + ux * headSize * 0.62 }
    headPoints = `${Q.x},${Q.y} ${a.x},${a.y} ${b.x},${b.y}`
  }
  return {
    path: `M${P.x} ${P.y}Q${c.x} ${c.y} ${Q.x} ${Q.y}`,
    head: headPoints,
    mid: { x: (P.x + 2 * c.x + Q.x) / 4, y: (P.y + 2 * c.y + Q.y) / 4 },
    c,
    q: Q,
  }
}

export function RulesOverlayBoardLayer({
  model,
  isolatedRow,
  territoryShapeById,
  regionShapeById,
  regionTerritoryIds,
  viewBox,
  unitsPerPixel: rawU,
  palette,
  idPrefix,
  compact,
}: RulesOverlayBoardLayerProps) {
  // Sizes are screen px at the fitted view; zoomed in past it, the overlay
  // grows with the board as the troop counts do, so arrows, gaps and tags keep
  // their place beside the counts (maintainer, 2026-11-03).
  const u = Math.max(rawU, Math.max(viewBox.width, viewBox.height) / 1400)
  const visible = model.marks.map((m) => markVisible(m, isolatedRow))
  const shapesOf = (m: { territoryIds: string[]; regionIds?: string[] }) => {
    const out: string[] = []
    for (const id of m.territoryIds) { const s = territoryShapeById[id]; if (s) out.push(s) }
    for (const rid of m.regionIds ?? []) {
      const s = regionShapeById[rid]
      if (s) out.push(s)
      else for (const id of regionTerritoryIds[rid] ?? []) { const t = territoryShapeById[id]; if (t) out.push(t) }
    }
    return out
  }
  const arrowWidth = (px: number) => px * u
  // Arrows are referenced by tags (their middle), so build every one first.
  const arrows = new Map<number, ArrowGeom>()
  model.marks.forEach((m, i) => {
    if (m.kind !== 'arrow') return
    const w = arrowWidth(m.widthPx)
    arrows.set(i, arrowGeometry(m.from, m.to, m.bulge, m.startGapPx * u, m.endGapPx * u, 4 * u + w * 2.3, m.head))
  })

  // The scrim reaches far past the board so a pan never reveals its edge.
  const big = { x: viewBox.x - viewBox.width * 4, y: viewBox.y - viewBox.height * 4, width: viewBox.width * 9, height: viewBox.height * 9 }
  const clips: React.ReactNode[] = []
  // What the overview lights, for tags that must not cover it.
  const litRings = model.marks.flatMap((m) => (m.kind === 'lit' && !m.detail ? shapesOf(m).map(pathRings) : []))
  // …and the overview's arrows, sampled along each curve.
  const arrowDots: Pt[] = []
  model.marks.forEach((m) => {
    if (m.kind !== 'arrow' || m.detail) return
    const c = { x: (m.from.x + m.to.x) / 2 - (m.to.y - m.from.y) * m.bulge, y: (m.from.y + m.to.y) / 2 + (m.to.x - m.from.x) * m.bulge }
    for (let k = 1; k < 12; k++) {
      const t = k / 12
      arrowDots.push({ x: (1 - t) ** 2 * m.from.x + 2 * (1 - t) * t * c.x + t * t * m.to.x, y: (1 - t) ** 2 * m.from.y + 2 * (1 - t) * t * c.y + t * t * m.to.y })
    }
  })
  const arrowNear = 6 * u
  const litHit = (p: Pt) => litRings.some((r) => insideRings(r, p)) || arrowDots.some((d) => Math.abs(d.x - p.x) < arrowNear && Math.abs(d.y - p.y) < arrowNear)
  const holes: React.ReactNode[] = []
  const lit: React.ReactNode[] = []
  const lines: React.ReactNode[] = []
  const tags: React.ReactNode[] = []
  model.marks.forEach((m: OverlayMark, i) => {
    if (!visible[i]) return
    if (m.kind === 'hole' || (m.kind === 'lit' && m.hole !== false)) {
      shapesOf(m).forEach((d, k) => holes.push(<Path key={`h${i}-${k}`} d={d} fill="#000" fillRule="evenodd" />))
    }
    if (m.kind === 'lit') {
      const shapes = shapesOf(m)
      // Never inside the territories lit on top of it (a mesa's towers, crystals).
      const clipId = m.clipOut?.length ? `${idPrefix}rules-clip-${i}` : null
      if (clipId) {
        const cut = m.clipOut!.map((id) => territoryShapeById[id]).filter(Boolean).join(' ')
        clips.push(
          <ClipPath key={clipId} id={clipId}>
            <Path d={`M${big.x} ${big.y}h${big.width}v${big.height}h${-big.width}Z ${cut}`} fillRule="evenodd" clipRule="evenodd" />
          </ClipPath>,
        )
      }
      lit.push(
        <G key={`l${i}`} clipPath={clipId ? `url(#${clipId})` : undefined}>
          {m.glow ? shapes.map((d, k) => (
            <Path key={`g${k}`} d={d} fill="none" stroke={m.color} strokeOpacity={0.35} strokeWidth={m.strokePx * 3 * u} strokeLinejoin="miter" strokeMiterlimit={4} />
          )) : null}
          {shapes.map((d, k) => (
            <Path
              key={k}
              d={d}
              fill={m.fillOpacity > 0 ? m.color : 'none'}
              fillOpacity={m.fillOpacity}
              fillRule="evenodd"
              stroke={m.color}
              strokeWidth={m.strokePx * u}
              strokeDasharray={m.dashed ? [4 * u, 3 * u] : undefined}
              // Mitred, so a mesa's octagon and a tower's cross keep their corners.
              strokeLinejoin="miter"
              strokeMiterlimit={4}
            />
          ))}
        </G>,
      )
    }
    if (m.kind === 'arrow') {
      const g = arrows.get(i)!
      const w = arrowWidth(m.widthPx)
      const dash = m.dash ? m.dash.split(' ').map((n) => Number(n) * u) : undefined
      lines.push(
        <G key={`a${i}`}>
          <Path d={g.path} fill="none" stroke="#000" strokeOpacity={0.6} strokeWidth={w * 1.7 + 1.2 * u} strokeLinecap="round" />
          <Path d={g.path} fill="none" stroke={m.color} strokeWidth={w} strokeLinecap="round" strokeDasharray={dash} />
          {g.head ? <Polygon points={g.head} fill={m.color} stroke="#000" strokeOpacity={0.8} strokeWidth={Math.max(1.4 * u, w * 0.4)} strokeLinejoin="round" /> : null}
        </G>,
      )
    }
    if (m.kind === 'ring') {
      lit.push(<Circle key={`r${i}`} cx={m.at.x} cy={m.at.y} r={m.radiusPx * u} fill="none" stroke={m.color} strokeWidth={2 * u} />)
    }
    if (m.kind === 'tag') {
      const base = 'arrowOf' in m.at ? arrows.get(m.at.arrowOf)?.mid : m.at
      if (!base) return
      const fs = compact ? 8.5 : 10.5
      const h = compact ? 16 : 20
      const at = { x: base.x + (m.dxPx ?? 0) * u, y: base.y + m.dyPx * u }
      // Web: the tag in words (react-native-svg passes ARIA attributes to the DOM).
      const label = (Platform.OS === 'web' && m.label ? { 'aria-label': m.label, role: 'img' } : {}) as object
      if (m.dice) {
        // "5 v 7": bold numbers, a smaller, lighter lowercase "v" on the same
        // baseline; a size up from the other tags so the dice read at a glance.
        const DICE_SCALE = 1.17
        const h = (compact ? 16 : 20) * DICE_SCALE
        const nfs = fs * 1.12 * DICE_SCALE
        const vfs = nfs * 0.7
        const a = String(m.dice.attack)
        const d = String(m.dice.defend)
        const aw = a.length * nfs * 0.72
        const dw = d.length * nfs * 0.72
        const vw = vfs * 0.62
        const gap = 4
        // With roles, each number is led by its side's glyph ("[plain] 5 v [tower] 7").
        const gs = m.roles ? h - 5 : 0
        const gg = m.roles ? 2 : 0
        const w = gs + gg + aw + vw + gs + gg + dw + gap * 2 + 16
        const x0 = -w / 2 + 8
        const y = nfs * 0.36
        const glyph = (role: { glyph: ChipGlyph; color: string }, x: number) => (
          <G transform={`translate(${x} ${-gs / 2}) scale(${gs / 24})`}>
            <GlyphShapes glyph={role.glyph} color={role.color} />
          </G>
        )
        const xa = x0 + gs + gg
        const xv = xa + aw + gap
        const xd = xv + vw + gap + gs + gg
        tags.push(
          <G key={`t${i}`} transform={`translate(${at.x} ${at.y}) scale(${u})`} {...label}>
            <Rect x={-w / 2} y={-h / 2} width={w} height={h} rx={h / 2} fill={palette.paper} stroke={m.color} strokeWidth={2} />
            {m.roles ? glyph(m.roles.attack, x0) : null}
            <SvgText x={xa + aw / 2} y={y} textAnchor="middle" fontFamily="Bungee" fontSize={nfs} fill={m.color}>{a}</SvgText>
            <SvgText x={xv + vw / 2} y={y} textAnchor="middle" fontFamily="Zilla Slab" fontSize={vfs} fill={m.color} fillOpacity={0.7}>v</SvgText>
            {m.roles ? glyph(m.roles.defend, xv + vw + gap) : null}
            <SvgText x={xd + dw / 2} y={y} textAnchor="middle" fontFamily="Bungee" fontSize={nfs} fill={m.color}>{d}</SvgText>
          </G>,
        )
        return
      }
      if (m.glyph) {
        // Text led by a rule glyph, a size up like the dice tags.
        const th = h * 1.25
        const tfs = fs * 1.15
        const gs = th - 6
        const w = m.text.length * tfs * 0.6 + gs + 16
        const spot = m.avoidLit ? placeClear(base, (w / 2) * u, (th / 2) * u, (compact ? 12 : 16) * u, litHit) : at
        tags.push(
          <G key={`t${i}`} transform={`translate(${spot.x} ${spot.y}) scale(${u})`} {...label}>
            <Rect x={-w / 2} y={-th / 2} width={w} height={th} rx={th / 2} fill={palette.paper} stroke={m.color} strokeWidth={2} />
            <G transform={`translate(${-w / 2 + 7} ${-gs / 2}) scale(${gs / 24})`}>
              <GlyphShapes glyph={m.glyph.glyph} color={m.glyph.color} />
            </G>
            <SvgText x={-w / 2 + 7 + gs + 4 + (w - gs - 18) / 2} y={tfs * 0.36} textAnchor="middle" fontFamily="Bungee" fontSize={tfs} fill={m.color}>
              {m.text}
            </SvgText>
          </G>,
        )
        return
      }
      const iconW = m.icon && TAG_ICON_PATHS[m.icon] ? h : 0
      const w = m.text.length * fs * 0.72 + iconW + 12
      tags.push(
        <G key={`t${i}`} transform={`translate(${at.x} ${at.y}) scale(${u})`} {...label}>
          <Rect x={-w / 2} y={-h / 2} width={w} height={h} rx={h / 2} fill={palette.paper} stroke={m.color} strokeWidth={1.6} />
          {iconW ? (
            <Path d={TAG_ICON_PATHS[m.icon!]} fill={m.color} transform={`translate(${-w / 2 + 4} ${-h / 2 + 2}) scale(${(h - 4) / 24})`} />
          ) : null}
          <SvgText x={iconW ? (h - 4) / 2 : 0} y={fs * 0.36} textAnchor="middle" fontFamily="Bungee" fontSize={fs} fill={m.color}>
            {m.text}
          </SvgText>
        </G>,
      )
    }
  })

  const maskId = `${idPrefix}rules-scrim`
  return (
    <G pointerEvents="none">
      <Defs>
        {clips}
        <Mask id={maskId} maskUnits="userSpaceOnUse" x={big.x} y={big.y} width={big.width} height={big.height}>
          <Rect x={big.x} y={big.y} width={big.width} height={big.height} fill="#fff" />
          {holes}
        </Mask>
      </Defs>
      <Rect x={big.x} y={big.y} width={big.width} height={big.height} fill={palette.scrim} fillOpacity={model.scrimStrength ?? 1} mask={`url(#${maskId})`} />
      {lit}
      {lines}
      {tags}
    </G>
  )
}
